import { fetchApi } from "@/lib/api/index.ts";
import { useQuery } from "@tanstack/react-query";

export enum ChurnType {
  AUTOMATED_DEACTIVATION = "AUTOMATED_DEACTIVATION",
  MANUAL_DEACTIVATION = "MANUAL_DEACTIVATION",
  MENTOR_SWITCH = "MENTOR_SWITCH",
}

export interface EngagementMetrics {
  totalMessages: number;
  lastMessageDate: string | null;
  engagementTrend: "declining" | "stable" | "improving" | "unknown";
  daysActive: number;
  averageMessagesPerDay: number;
  lastEngagementWeeks: number[];
}

export interface ChurnAnalysisMetadata {
  processingDuration?: number;
  avaCompletedAt?: string;
  confidenceScore?: number;
  analysisVersion?: string;
  dataQuality?: "high" | "medium" | "low";
  [key: string]: any;
}

export interface ChurnAnalysis {
  id: string;
  mentorshipId: string;
  userId: string;
  churnType: ChurnType;
  primaryReason: string;
  churnDate: string;
  avaAnalysis: string | null;
  engagementMetrics: EngagementMetrics;
  analysisMetadata: ChurnAnalysisMetadata;
  created_at: string;
  updated_at: string;
  mentorshipDetails: {
    startDate: string;
    duration: number;
  };
}

export const useGetChurnAnalysis = (mentorshipId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["churn-analysis", mentorshipId],
    queryFn: async () => {
      const response = await fetchApi<ChurnAnalysis>({
        url: `/v2/mentor/mentorship/${mentorshipId}/churn-analysis`,
        method: "GET",
      });

      if (!response.data) {
        throw new Error("No churn analysis found for this mentorship");
      }

      return response.data;
    },
    enabled: !!mentorshipId && enabled,
    retry: false, // Don't retry if churn analysis doesn't exist
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};
